/**
 * Watches a chat's message list for tool results that should mutate the tasks
 * cache as soon as the message arrives (rather than waiting for the next poll
 * tick). Three families share this sweep:
 *  - get_task_status / get_task_result on MCP tasks → update status.
 *  - a result carrying `task_id` whose tool has no long-task binding, i.e. an
 *    ordinary MCP task, whose row the backend creates (ADR 063) → refetch the
 *    tasks list so the row lands in the session cache.
 *  - long-task tools (routed by role from the lifecycle map, ADR 059):
 *    - submit → register a kind: "long-task" row keyed by job_id.
 *    - status → advance the cache the moment the persona reports a terminal status.
 *    - fetch → register the hidden fetch tracker.
 */
import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import * as types from "types";
import { applyLongTaskTerminal } from "hooks/tasks/longTaskTerminal";
import {
  buildLongTaskFetchEntry,
  buildLongTaskSubmitEntry,
} from "hooks/tasks/longTaskBuilders";
import { makeTaskCacheOps } from "hooks/tasks/useTaskPolling";
import { updateTaskStatus } from "hooks/tasks/taskCacheWrites";
import { FETCH_TRACKER_MIN_POLL_INTERVAL } from "hooks/tasks/taskPollConstants";
import {
  getLongTaskGroup,
  longTaskGroupsLoadAtom,
  mcpConfigSignature,
  moduleGroupMap,
} from "hooks/tasks/useLongTaskGroups";
import { pendingInjectionsAtom } from "hooks/tasks/usePendingInjections";
import { isTaskStatusValue, isKnownTerminalStatus } from "types/TaskRegistry";
import type { TerminalTaskStatus } from "types/TaskRegistry";
import { parseLongTaskResult } from "utilities/longTaskResult";
import { hasCommittedTerminalDirective } from "./useChatLongTaskLifecycle";
import type { Task, TaskCreate } from "types/Task";
import type { McpConfig } from "types/Mcp";
import { TASKS_QUERY_KEY } from "services/dbApi/hooks/useTasksQuery";
import usePostTaskMutation, {
  POST_TASK_MUTATION_KEY,
} from "services/dbApi/hooks/usePostTaskMutation";
import usePatchTaskMutation, {
  PATCH_TASK_MUTATION_KEY,
} from "services/dbApi/hooks/usePatchTaskMutation";
import useClaimTerminalTaskMutation from "services/dbApi/hooks/useClaimTerminalTaskMutation";
import { useTaskWriteInFlight } from "hooks/tasks/useTaskWriteInFlight";
import { useAtomValue, useSetAtom } from "jotai";

/** Module-level set of jobIds whose POST is currently in flight, so a re-running
 *  sweep (or a second mount of the same chat) does not POST the same job twice.
 *  Intentionally module-scoped (spans chats and component mounts); safe because
 *  jobId is globally unique, so no cross-chat collision is possible. */
const inFlightRegistrations = new Set<string>();

/** The sweep's effect dependency: the joined uuids of the tool-result messages. */
function deriveToolResultSignature(messages: readonly types.Message[]): string {
  return messages
    .filter(types.isToolResultMessage)
    .map((m) => m.uuid)
    .join("\n");
}

interface UseToolResultSweepOptions {
  /**
   * Current message list. Gates the effect via its tool-result signature rather
   * than by identity.
   *
   * During an assistant stream `setMessages` fires a new array per chunk (mostly
   * text deltas), while the sweep only ever acts on tool-result messages, each
   * processed exactly once. Keying on the signature collapses per-token effect
   * re-runs to per-tool-result runs without changing when results are handled: a
   * result is still swept the first render it appears.
   */
  messages: types.Message[];
  threadId: string;
  /**
   * The active persona's MCP config. Used only to tell whether the long-task
   * lifecycle map is current, so a result whose tool binding is unresolved can
   * be deferred instead of misrouted.
   */
  mcpConfig: McpConfig | undefined;
}

const TASK_STATUS_TOOLS = new Set(["get_task_status", "get_task_result"]);

export function useToolResultSweep({
  messages,
  threadId,
  mcpConfig,
}: UseToolResultSweepOptions): void {
  const toolResultSignature = React.useMemo(
    () => deriveToolResultSignature(messages),
    [messages]
  );
  // Every tool result already in the message list when this chat mounted is
  // history, not something to act on. `messages` is the committed list from the
  // very first render (useCurrentMessages reads a suspense query), so seeding
  // here is equivalent to a first-pass effect and needs no "have we swept yet"
  // flag: seeding completes during render, before any effect anywhere runs.
  //
  // Held in state despite never being re-set, because both ref forms are worse:
  // `useRef(new Set(...))` rebuilds the Set on every render (once per streamed
  // token) just to discard it, and the lazy-ref alternative writes `ref.current`
  // during render. This lazy initializer runs exactly once.
  const [processedToolResults] = React.useState(
    () => new Set(messages.filter(types.isToolResultMessage).map((m) => m.uuid))
  );
  // Read the live list from the effect without depending on it, so the signature
  // gate above stays the sole trigger.
  const readMessages = React.useEffectEvent(() => messages);
  // The lifecycle map is a module-level mirror, invisible to React, so the effect
  // needs this atom to learn when it hydrates. Without it, a result swept while
  // the map was still loading would never be re-evaluated.
  //
  // A failed load is deliberately *not* ready here, even though it counts as
  // settled: it leaves an empty map, and treating that as authoritative would read
  // every long-task tool as unbound and permanently discard its routing. Deferring
  // instead leaves those results unprocessed and recoverable on the next
  // successful load.
  const groupsLoad = useAtomValue(longTaskGroupsLoadAtom);
  const activeConfigSignature = mcpConfigSignature(mcpConfig);
  const groupsReady =
    groupsLoad?.signature === activeConfigSignature && !groupsLoad.failed;
  const queryClient = useQueryClient();
  // Destructured because React Query binds these once per observer; the result
  // object itself is recreated each render, so only the members are safe deps.
  const { mutate: postTask } = usePostTaskMutation();
  const { mutate: patchTask } = usePatchTaskMutation();
  const { mutateAsync: claimTask } = useClaimTerminalTaskMutation();
  const setPendingInjections = useSetAtom(pendingInjectionsAtom);
  // Re-trigger for the cross-pass write race: a live subscription, so the effect
  // re-runs the moment an in-flight task write settles. The same-pass race is
  // handled separately by the imperative isMutating read below.
  const taskWriteInFlight = useTaskWriteInFlight();

  React.useEffect(() => {
    const messages = readMessages();

    // Index tool-call messages by id once per pass so the per-result lookups
    // below are O(1). A linear `messages.find` per tool-result made the sweep
    // O(results × messages), which grows quadratically in long chats.
    const toolCallById = new Map(
      messages.filter(types.isToolCallMessage).map((m) => [m.id, m] as const)
    );
    const findToolCall = (toolCallId: string) => toolCallById.get(toolCallId);

    // Read the live query cache on each call so this pass sees rows a just-
    // dispatched write/register added, unlike the poller's tasksRef snapshot.
    const getTasks = (): Task[] =>
      queryClient.getQueryData<Task[]>(TASKS_QUERY_KEY) ?? [];

    const { getTask, setTask, commitTask, claimTerminal, enqueueInjection } =
      makeTaskCacheOps({
        getTasks,
        queryClient,
        patchTask,
        claimTask,
        setPendingInjections,
      });

    const registerTask = (create: TaskCreate) => {
      if (inFlightRegistrations.has(create.jobId)) return;
      inFlightRegistrations.add(create.jobId);
      postTask(create, {
        onSettled: () => {
          inFlightRegistrations.delete(create.jobId);
        },
      });
    };

    // Shared dependency bundle for applyLongTaskTerminal; both the status and
    // fetch branches drive the same terminal path.
    const terminalDeps = {
      getTask,
      commitTask,
      claimTerminal,
      enqueue: enqueueInjection,
    };

    /**
     * Drives the terminal path, un-marking the source message on failure so a
     * later sweep (triggered by a messages change) reattempts. If messages stay
     * static, the central poller is the backstop.
     */
    const applyTerminalAndUnmarkOnFailure = (
      uuid: string,
      jobId: string,
      status: TerminalTaskStatus,
      statusReason: string | null,
      statusRaw?: string
    ) => {
      void applyLongTaskTerminal(jobId, status, {
        statusReason,
        statusRaw,
        deps: terminalDeps,
      }).catch((err) => {
        console.error(err);
        processedToolResults.delete(uuid);
      });
    };

    // uuids of submit results whose backend-created row is not yet in cache.
    // The refetch that discovers them is issued once after the loop (batched)
    // and write-gated there, so it never fires per-message and never races a
    // write. These stay unprocessed until the refetch actually fires: if it is
    // deferred (a write is in flight), the next pass must re-evaluate them, so
    // marking them processed now would silently drop the deferred refetch.
    const pendingDiscovery: string[] = [];

    for (const msg of messages) {
      if (!types.isToolResultMessage(msg)) continue;
      if (processedToolResults.has(msg.uuid)) continue;

      if (TASK_STATUS_TOOLS.has(msg.tool_name)) {
        // get_task_status returns a structured key=value line so status= may be
        // preceded by other fields ("Task info: status=..."). get_task_result
        // is free-form text that may embed status= in prose; only match when
        // the field leads the content (after optional whitespace) so incidental
        // mentions don't produce bogus updates.
        const regex =
          msg.tool_name === "get_task_status"
            ? /\bstatus=(\w+)/
            : /^\s*status=(\w+)/;
        const statusMatch = msg.content.match(regex);
        const rawStatus = statusMatch?.[1];

        // Always mark processed: an unrecognizable result won't improve on
        // retry; the poller is the fallback.
        processedToolResults.add(msg.uuid);
        // isTaskStatusValue is a type guard, so rawStatus narrows to
        // TaskStatusValue past this point, so no cast is needed.
        if (!rawStatus || !isTaskStatusValue(rawStatus)) continue;

        const toolCall = findToolCall(msg.tool_call_id);
        // For an MCP-task row the row's jobId is its API task_id, so the API
        // `task_id` is the jobId `updateTaskStatus` keys on.
        const jobId = types.readToolInputString(toolCall, "task_id");
        if (!jobId) continue;

        updateTaskStatus({ jobId, status: rawStatus, setTask });
        continue;
      }

      const binding = moduleGroupMap.byToolName[msg.tool_name];

      if (!binding) {
        // An unresolved binding is only meaningful once the lifecycle map is
        // current. While it is stale or unhydrated, every long-task tool looks
        // unbound, and treating this as the backend-created-row case below would
        // mark the result processed and permanently discard its real routing
        // (submit registration, terminal status, fetch links). Leave it for the
        // pass that runs when `groupsReady` flips.
        if (!groupsReady) continue;
        // An unbound tool carrying a task_id is an ordinary MCP task, the one
        // kind whose row the backend creates (ADR 063); long-task rows are
        // POSTed from the role branches below. Defer to the batched,
        // write-gated refetch after the loop rather than invalidating inline: a
        // GET landing mid-write can overwrite a just-committed field
        // (mergeTasksPreservingClientState only preserves status/pollExhausted/
        // liveConfirmed). An already-cached row is marked processed (nothing to
        // discover); a missing row is left unprocessed and queued.
        if (msg.task_id) {
          const inCache = getTasks().some((j) => j.jobId === msg.task_id);
          if (inCache) {
            processedToolResults.add(msg.uuid);
          } else {
            pendingDiscovery.push(msg.uuid);
          }
        }
        continue;
      }
      // Left unprocessed when the group is unresolved, so a later pass retries
      // once the lifecycle map hydrates. Must stay above the mark below.
      const group = getLongTaskGroup(binding.server, binding.group);
      if (!group) continue;

      // Every long-task role marks the result processed and skips an error, so
      // both gates live here rather than in each branch.
      processedToolResults.add(msg.uuid);
      if (msg.isError) continue;

      if (binding.role === "submit") {
        const parsed = parseLongTaskResult(msg.content, "submit");
        if (!parsed) continue;

        // Skip if the job is already in cache, e.g. a peer tab registered it.
        const alreadyCached = getTasks().some((j) => j.jobId === parsed.job_id);
        if (!alreadyCached) {
          registerTask(
            buildLongTaskSubmitEntry(parsed, group, msg.tool_name, threadId)
          );
        }
      } else if (binding.role === "status") {
        const parsed = parseLongTaskResult(msg.content, "status");
        if (!parsed) continue;
        if (!isTaskStatusValue(parsed.status)) continue;

        const jobId = types.readToolInputString(
          findToolCall(msg.tool_call_id),
          "job_id"
        );
        if (!jobId) continue;

        if (isKnownTerminalStatus(parsed.status)) {
          // A committed directive means some actor already won this job's claim
          // and announced it, so there is nothing left for the terminal path to
          // do: the claim would return claimed=false and the enqueue would be
          // deduped anyway. Skipping saves the round-trip. It also means the
          // sweep does not commit terminal status for this row — acceptable
          // because a directive can only have been committed after a terminal
          // claim, and that path already committed the status.
          if (hasCommittedTerminalDirective(messages, jobId)) continue;
          applyTerminalAndUnmarkOnFailure(
            msg.uuid,
            jobId,
            parsed.status,
            parsed.reason,
            parsed.raw_status
          );
        } else {
          updateTaskStatus({
            jobId,
            status: parsed.status,
            statusReason: parsed.reason,
            statusRaw: parsed.raw_status,
            setTask,
          });
        }
      } else if (binding.role === "fetch") {
        const jobId = types.readToolInputString(
          findToolCall(msg.tool_call_id),
          "job_id"
        );
        if (!jobId) continue;

        // A fetch tool is declared `TaskConfig(mode="required")`, so the API
        // always answers with a deferred task handle and never with the results
        // themselves. Register a hidden mcp-task entry so the poller drives the
        // handle to completion; `applyFetchTaskTerminal` is what stores the links
        // and re-prompts the agent to present them.
        if (msg.task_id) {
          registerTask(
            buildLongTaskFetchEntry({
              fetchTaskId: msg.task_id,
              ownerJobId: jobId,
              toolName: msg.tool_name,
              threadId,
              serverUrl: group.server,
              groupName: group.group,
              pollInterval:
                msg.task_poll_interval ?? FETCH_TRACKER_MIN_POLL_INTERVAL,
            })
          );
        } else {
          // Error level so Datadog collects it: with no tracker the fetch is
          // never observed, so the job's results are never harvested unless the
          // user clicks the card's button by hand.
          console.error(
            `Long-task fetch result for job ${jobId} carried no task_id; ` +
              `cannot track the background fetch. User can retry via the card.`
          );
        }
      }
    }

    // Batched discovery refetch. Gated on an imperative isMutating read (not the
    // render-time taskWriteInFlight snapshot) so it also sees a POST/PATCH this
    // same pass started: commitTask/registerTask above dispatch a mutation's
    // pending state synchronously, but the snapshot was captured at render. A GET
    // resolving mid-write could overwrite a just-committed field, so defer when
    // any task write is in flight: the effect re-runs when taskWriteInFlight (in
    // deps) flips false, and the still-unprocessed uuids re-queue the refetch.
    if (pendingDiscovery.length > 0) {
      const writeInFlight =
        queryClient.isMutating({ mutationKey: POST_TASK_MUTATION_KEY }) > 0 ||
        queryClient.isMutating({ mutationKey: PATCH_TASK_MUTATION_KEY }) > 0;
      if (!writeInFlight) {
        for (const uuid of pendingDiscovery) {
          processedToolResults.add(uuid);
        }
        queryClient.invalidateQueries({ queryKey: TASKS_QUERY_KEY });
      }
    }
  }, [
    toolResultSignature,
    threadId,
    groupsReady,
    processedToolResults,
    setPendingInjections,
    queryClient,
    postTask,
    patchTask,
    claimTask,
    taskWriteInFlight,
  ]);
}
